/**
 * «Plantillas de WhatsApp en Meta», al pie de Mensajes (la parte C del 01/11/2026). Alan: si el canal es WhatsApp, se
 * crean las plantillas de los grupos, una por grupo, y **nada va a Meta sin que el operador lo pida**.
 *
 * - Lista los grupos que van por el WhatsApp oficial con el estado de su plantilla: «Para crear», «En revisión de Meta»,
 *   «Aprobada» o «Rechazada: motivo» (con «Rehacerla», que abre el grupo para escribir otra con IA o corregirla).
 * - «Crear las plantillas en Meta» pide confirmación antes: cuántas plantillas (una por texto), que Meta tarda de
 *   minutos a 48 h, que cada una ocupa un lugar del cupo de la cuenta y que una vez mandada no se edita (se duplica).
 *   Con cambios sin guardar, guarda primero: la propuesta que vale es la guardada.
 * - El estado lo traen el polling del oficial y «Consultar a Meta», que llama a lo mismo que el botón del oficial.
 * - Lo de siempre no cambia: Mensajes y «Preparar el envío» dejan seguir con plantillas en revisión, y el asistente del
 *   oficial no deja mandar hasta que estén aprobadas.
 */

import { useEffect, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Clock, Loader2, RefreshCw, Send } from "lucide-react";
import { btnPrimario, btnSecundario } from "../../segmentaciones/components/PasosCampana";
import { oficialApi } from "../../wa-oficial/api";
import { llamar } from "../../wa-oficial/components/base/comun";
import { ESPERA_CONSULTA_MANUAL_MS } from "../../wa-oficial/components/EstadoPlantillaEnVivo";
import { ErrorTarjeta } from "../../wa-oficial/components/estadisticas/comun";
import { textoError } from "../../wa-oficial/logic/respuesta";
import type { PlantillaDetalle } from "../../wa-oficial/types";
import { mensajesApi } from "../api";
import type { Borrador } from "../logic/mensajes";
import {
  cuantasPlantillas,
  estadoDelGrupo,
  gruposPorOficial,
  plantillasEnPalabras,
  porCrear,
  resultadosParaMirar,
  textoDelEstado,
  type Creadas,
  type EstadoConocido,
  type EstadoDelGrupo,
} from "../logic/plantillas";
import type { RespuestaDePlantillas, VistaDeMensajes } from "../types";

const btnChico =
  "min-h-[44px] px-3.5 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 hover:bg-slate-50 inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400";

const CHIP: Record<EstadoDelGrupo["tipo"], string> = {
  sin_propuesta: "bg-slate-100 text-slate-600",
  para_crear: "bg-violet-50 text-violet-800",
  sin_llegar: "bg-slate-100 text-slate-700",
  en_revision: "bg-amber-50 text-amber-800",
  aprobada: "bg-emerald-50 text-emerald-800",
  rechazada: "bg-red-50 text-red-700",
  consultando: "bg-slate-100 text-slate-600",
};

function Icono({ tipo }: { tipo: EstadoDelGrupo["tipo"] }) {
  if (tipo === "aprobada") return <CheckCircle2 size={15} className="flex-none" aria-hidden="true" />;
  if (tipo === "en_revision") return <Clock size={15} className="flex-none" aria-hidden="true" />;
  if (tipo === "rechazada") return <AlertTriangle size={15} className="flex-none" aria-hidden="true" />;
  return null;
}

export function PlantillasDeLosGrupos({
  campanaId,
  vista,
  borradores,
  creadas,
  conocido,
  cupo,
  sinGuardar,
  guardar,
  alCrear,
  alElegirGrupo,
  alConsultar,
}: {
  campanaId: string;
  vista: Pick<VistaDeMensajes, "grupos" | "whatsapp">;
  borradores: Record<string, Borrador>;
  creadas: Creadas;
  /** el estado de una plantilla, de la lista de la marca o de la respuesta de crear */
  conocido: (id: string) => EstadoConocido | undefined;
  /** las plantillas que usa la cuenta de WhatsApp y cuántas deja Meta; null si todavía no se sabe */
  cupo: { usadas: number; limite: number } | null;
  sinGuardar: boolean;
  /** guarda el borrador antes de crear (la propuesta que vale es la guardada); false si no se pudo */
  guardar: () => Promise<boolean>;
  alCrear: (r: RespuestaDePlantillas) => void;
  alElegirGrupo: (grupoId: string) => void;
  /** vuelve a pedir la lista de plantillas de la marca */
  alConsultar: () => Promise<void>;
}) {
  const [confirmando, setConfirmando] = useState(false);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState("");
  const [ultima, setUltima] = useState<RespuestaDePlantillas | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [descansa, setDescansa] = useState(false);
  const [errorConsulta, setErrorConsulta] = useState("");
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  const ids = gruposPorOficial(vista, borradores);
  if (!ids.length) return null;
  const nombreDe = (id: string) => vista.grupos.find((g) => g.id === id)?.nombre ?? id;
  const filas = ids.map((id) => ({ id, nombre: nombreDe(id), b: borradores[id], estado: estadoDelGrupo(borradores[id], creadas[id], conocido) }));
  const pendientes = filas.filter((f) => porCrear(f.estado)).map((f) => f.id);
  const cuantas = cuantasPlantillas(pendientes, borradores);
  const quedan = cupo ? Math.max(0, cupo.limite - cupo.usadas) : null;
  const noAlcanza = quedan !== null && cuantas > quedan;
  // las plantillas en revisión de estos grupos (la de su propuesta o la elegida a mano): «Consultar a Meta»
  const enRevision = [
    ...new Set(
      filas.flatMap((f) => {
        const propia = f.estado.tipo === "en_revision" ? [creadas[f.id].plantillaId] : [];
        const elegida = f.b.plantillaId && ["received", "pending"].includes(String(conocido(f.b.plantillaId)?.estado)) ? [f.b.plantillaId] : [];
        return [...propia, ...elegida];
      }),
    ),
  ];

  const crear = async () => {
    setCreando(true);
    setError("");
    if (sinGuardar && !(await guardar())) {
      if (!vivo.current) return;
      setCreando(false);
      setConfirmando(false);
      setError("No se pudo guardar el borrador, y sin guardar no se crean las plantillas: mirá el error de arriba.");
      return;
    }
    const l = await llamar<RespuestaDePlantillas>(() => mensajesApi.plantillas(campanaId));
    if (!vivo.current) return;
    setCreando(false);
    setConfirmando(false);
    if (!l.ok) {
      setError(textoError(l));
      return;
    }
    setUltima(l.data);
    alCrear(l.data);
  };

  const consultar = async () => {
    setConsultando(true);
    setErrorConsulta("");
    let fallo = "";
    for (const id of enRevision) {
      const l = await llamar<PlantillaDetalle>(() => oficialApi.sincronizarPlantilla(id));
      if (!l.ok && !fallo) fallo = textoError(l);
    }
    await alConsultar();
    if (!vivo.current) return;
    setConsultando(false);
    setErrorConsulta(fallo ? `No pudimos consultar alguna a Meta. ${fallo}` : "");
    // Twilio no tiene nada nuevo cada segundo: el botón descansa un rato, como el del oficial
    setDescansa(true);
    setTimeout(() => {
      if (vivo.current) setDescansa(false);
    }, ESPERA_CONSULTA_MANUAL_MS);
  };

  const paraMirar = ultima ? resultadosParaMirar(ultima) : [];

  return (
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" aria-labelledby="plantillas-de-meta" data-testid="plantillas-de-los-grupos">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex-[1_1_320px] space-y-1">
          <h2 id="plantillas-de-meta" className="text-lg font-bold text-slate-900">
            Plantillas de WhatsApp en Meta
          </h2>
          <p className="text-sm text-slate-600">
            Cada grupo que va por WhatsApp oficial sale con una plantilla aprobada por Meta. Con la propuesta de cada grupo se crean y se mandan a aprobar de una vez, sólo cuando lo pedís.
          </p>
        </div>
        <div className="flex flex-col items-start gap-1.5">
          <button type="button" className={btnPrimario} disabled={!pendientes.length || creando} onClick={() => setConfirmando(true)}>
            {creando ? <Loader2 size={16} className="animate-spin" aria-hidden="true" /> : <Send size={16} aria-hidden="true" />}
            {creando ? "Creando…" : "Crear las plantillas en Meta"}
          </button>
          {!pendientes.length && (
            <p className="text-[13px] text-slate-500" data-testid="nada-para-crear">
              {filas.some((f) => f.estado.tipo !== "sin_propuesta") ? "Todas las propuestas ya tienen su plantilla." : "Ningún grupo tiene una propuesta: escribila con IA en cada grupo."}
            </p>
          )}
        </div>
      </div>

      <ul className="divide-y divide-slate-100 rounded-xl border border-slate-100" aria-label="Plantilla de cada grupo">
        {filas.map((f) => (
          <li key={f.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`plantilla-grupo-${f.id}`}>
            <div className="min-w-0 flex-[1_1_240px] space-y-1">
              <p className="text-[15px] font-semibold text-slate-900">{f.nombre}</p>
              <p className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium ${CHIP[f.estado.tipo]}`}>
                <Icono tipo={f.estado.tipo} />
                {textoDelEstado(f.estado, f.b)}
              </p>
              {"nombre" in f.estado && f.estado.nombre && <p className="text-[13px] text-slate-500">«{f.estado.nombre}»</p>}
            </div>
            {(f.estado.tipo === "rechazada" || f.estado.tipo === "sin_propuesta") && (
              <button type="button" className={btnChico} onClick={() => alElegirGrupo(f.id)}>
                {f.estado.tipo === "rechazada" ? "Rehacerla" : "Ir al grupo"}
              </button>
            )}
          </li>
        ))}
      </ul>

      {enRevision.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          <button type="button" className={btnChico} onClick={() => void consultar()} disabled={consultando || descansa}>
            {consultando ? <Loader2 size={15} className="animate-spin" aria-hidden="true" /> : <RefreshCw size={15} aria-hidden="true" />}
            Consultar a Meta
          </button>
          <p className="text-[13px] text-slate-500">Meta tarda de minutos a 48 h. El estado se actualiza solo cada tanto; con esto lo pedís ahora.</p>
        </div>
      )}
      {errorConsulta && <ErrorTarjeta texto={errorConsulta} />}

      {confirmando && (
        <div role="alertdialog" aria-labelledby="confirmar-plantillas" aria-describedby="confirmar-plantillas-detalle" className="space-y-3 rounded-xl border border-violet-200 bg-violet-50/60 p-4" data-testid="confirmar-plantillas">
          <p id="confirmar-plantillas" className="text-base font-bold text-slate-900">
            ¿Crear {plantillasEnPalabras(cuantas)} en Meta?
          </p>
          <ul id="confirmar-plantillas-detalle" className="list-disc space-y-1.5 pl-5 text-sm text-slate-700">
            <li>
              {cuantas === pendientes.length
                ? `Una por grupo: ${pendientes.map(nombreDe).join(", ")}.`
                : `Para ${pendientes.map(nombreDe).join(", ")}: los grupos con el mismo texto comparten una.`}
            </li>
            <li>Se mandan a aprobar a Meta, que tarda de unos minutos a 48 h. Mientras tanto podés seguir y preparar el envío: la campaña sale recién cuando las aprueben.</li>
            <li data-testid="confirmar-cupo">
              {cupo
                ? `Cada plantilla ocupa un lugar de las ${cupo.limite} de tu cuenta de WhatsApp: hoy usás ${cupo.usadas}${noAlcanza ? "." : `; quedarían ${(quedan ?? 0) - cuantas} libres.`}`
                : "Cada plantilla ocupa un lugar de las que Meta deja tener en la cuenta de WhatsApp (250, salvo con el portfolio verificado)."}
            </li>
            <li>Una vez mandada, una plantilla no se edita: para cambiarla se duplica y sale como una nueva.</li>
            {sinGuardar && <li>Antes se guarda el borrador: la propuesta que vale es la guardada.</li>}
          </ul>
          {noAlcanza && (
            <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800" role="alert">
              No alcanza: {quedan === 1 ? "queda 1 lugar" : `quedan ${quedan} lugares`} y hacen falta {cuantas}. Borrá en Plantillas las que ya no uses.
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" className={btnPrimario} onClick={() => void crear()} disabled={creando || noAlcanza} autoFocus>
              {creando && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
              Crear y mandar a Meta
            </button>
            <button type="button" className={btnSecundario} onClick={() => setConfirmando(false)} disabled={creando}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {error && <ErrorTarjeta texto={error} />}
      {ultima && (
        <div className="space-y-2" role="status" data-testid="resultado-plantillas">
          <p className="text-sm font-semibold text-slate-800">
            {ultima.mandadas > 0 ? `Se ${ultima.mandadas === 1 ? "mandó 1 plantilla" : `mandaron ${ultima.mandadas} plantillas`} a aprobar a Meta.` : "No se mandó ninguna plantilla nueva a Meta."}
          </p>
          {paraMirar.length > 0 && (
            <ul className="space-y-2">
              {paraMirar.map((g) => (
                <li key={g.grupoId} className="rounded-xl border border-amber-100 bg-amber-50 px-3.5 py-2.5 text-sm text-amber-900">
                  <p>
                    <strong>{g.nombre}:</strong> {g.texto}
                  </p>
                  {g.reglas.length > 0 && (
                    <ul className="mt-1 list-disc pl-5">
                      {g.reglas.map((r) => (
                        <li key={r.id}>{r.mensaje}</li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
